import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { Modal } from "../../common/Modal/Modal";
import { Heading } from "../../common/Heading/Heading";
import InputField from "../../common/InputField/InputField";
import { useTranslation } from "react-i18next";
import { useData } from "../../../context/DataContext";
import { Button } from "../../common/Button/Button";

export const OrganizationModal = ({ isOpen, onClose, organization = null }) => {
  const { t } = useTranslation();
  const {
    createOrganization,
    updateOrganization,
    isLoading: contextLoading,
  } = useData();

  const isEditMode = !!organization;

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (organization) {
      setName(organization.name || "");
      setDescription(organization.description || "");
    } else {
      resetForm();
    }
  }, [organization, isOpen]);

  const handleSubmit = async () => {
    setError(null);
    setIsLoading(true);

    if (!name.trim()) {
      setError(t("common.name") + " " + t("common.isRequired"));
      setIsLoading(false);
      return;
    }

    const data = {
      name,
      description,
      ...(isEditMode && { id: organization.id }),
    };

    const action = isEditMode ? updateOrganization : createOrganization;
    const result = await action(data);
    setIsLoading(false);

    if (result?.success) {
      resetForm();
      onClose();
    } else {
      const errorKey = isEditMode
        ? "organizations.failedToUpdateOrganization"
        : "organizations.failedToCreateOrganization";
      setError(result?.message || t(errorKey));
    }
  };

  const resetForm = () => {
    setName("");
    setDescription("");
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const getHeadingText = () =>
    isEditMode
      ? t("organizations.editOrganization")
      : t("organizations.createOrganization");
  const getButtonText = () => {
    if (isLoading) {
      return isEditMode ? t("common.updating") : t("common.creating");
    }
    return isEditMode
      ? t("organizations.updateOrganization")
      : t("organizations.createOrganization");
  };

  return (
    <StyledModal isOpen={isOpen} onClose={handleClose}>
      <ModalContent>
        <ModalHeader>
          <Heading text={getHeadingText()} size={2} marginBottom="0" />
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label>{t("common.name")}</Label>
            <InputField
              type="text"
              placeholder={t("organizations.enterOrganizationName")}
              value={name}
              onChange={setName}
              required
            />
            {error && <ErrorMessage>{error}</ErrorMessage>}
          </FormGroup>

          <FormGroup>
            <Label>{t("common.description")}</Label>
            <StyledTextArea
              placeholder={t("organizations.enterOrganizationDescription")}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
            />
          </FormGroup>

          <ButtonGroup>
            <Button
              onClick={handleClose}
              variant="red"
              text={t("common.cancel")}
            />

            <Button
              disabled={isLoading || contextLoading}
              text={getButtonText()}
              onClick={handleSubmit}
            />
          </ButtonGroup>
        </Form>
      </ModalContent>
    </StyledModal>
  );
};

const StyledModal = styled(Modal)``;

const ModalContent = styled.div`
  width: 100%;
  max-width: 500px;
  background: white;
  border-radius: 8px;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div``;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
  color: #0c0c0e;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #e4def9;
  border-radius: 12px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #4a25d5;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
`;
